// Core
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  Crumbs,
  DateRangePopover,
  PageHead,
  SideTabs,
  Tabs,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import UploadHelpButton from "components/UploadHelpButton";

// Sub-views
import BilateralPricesCharts from "pages/ManageBilateralPrices/Charts";
import ListBilateralPrices from "pages/ManageBilateralPrices/List";
import ManageFixedBilateralPrices from "pages/ManageBilateralPrices/Fixed";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewBilateralPricesMutation } from "state/bilateralPrices/bilateralPricesApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

const TASK_TYPE = "BILATERAL_PRICES";
const HREF_TEMPLATE = "/docs/template_bilateral_prices.csv";

function ManageBilateralPrices() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);
  const { startDate, endDate, granularity, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [side, setSide] = useState("variable");
  const [tab, setTab] = useState("charts");
  const [providerUserId, setProviderUserId] = useState("");
  const fileInputRef = useRef(null);

  // CREATE Bilateral Prices (CSV upload + task-status polling)
  const activeTasks = useSelector(selectActiveTasksByType(TASK_TYPE));
  const [addBilateralPrices] = useAddNewBilateralPricesMutation();
  const handleUpload = handleFileUpload(addBilateralPrices, community.id, dispatch, TASK_TYPE);

  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE,
  });

  const isUploading = activeTasks.length > 0;

  const sideTabs = [
    { id: "variable", icon: "price_change", label: t("bilateralPrices.title") },
    { id: "fixed", icon: "lock_clock", label: t("fixedBilateralPrices.title") },
  ];

  const variableActions = (
    <div className="flex items-center gap-[10px] flex-wrap">
      <DateRangePopover
        value={{ start: startDate, end: endDate }}
        onChange={(v) =>
          dispatch(
            updateTimeSelector({
              startDate: v.start,
              endDate: v.end,
              granularity,
              horizon,
              isExpanded,
            })
          )
        }
      />
      <span className="w-px self-stretch bg-border my-1 mx-[2px]" />
      {isUploading && (
        <Chip tone="amber">
          <Icon name="hourglass_top" style={{ fontSize: 13 }} />
          {t("actions.uploading", { defaultValue: "A carregar…", ns: "common" })}
        </Chip>
      )}
      <Button variant="ghost" onClick={() => window.open(HREF_TEMPLATE)}>
        <Icon name="download" />
        {t("actions.template", { defaultValue: "Template", ns: "common" })}
      </Button>
      <UploadHelpButton size="md" />
      <Button variant="ghost" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>
        <Icon name="upload_file" />
        {t("actions.upload", { defaultValue: "Carregar", ns: "common" })}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleUpload}
      />
    </div>
  );

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="bilateral-prices" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("bilateralPrices.title") }]} />
      <PageHead
        title={t("bilateralPrices.title")}
        subtitle={t("bilateralPrices.subtitle")}
        actions={side === "variable" ? variableActions : null}
      />

      <SideTabs items={sideTabs} active={side} onChange={setSide}>
        {side === "variable" && (
          <div>
            <Tabs
              tabs={[
                { id: "charts", label: t("bilateralPrices.tabs.charts"), icon: "show_chart" },
                { id: "table", label: t("bilateralPrices.tabs.table"), icon: "table_rows" },
              ]}
              active={tab}
              onChange={setTab}
            />
            {tab === "charts" && (
              <BilateralPricesCharts
                providerUserId={providerUserId}
                setProviderUserId={setProviderUserId}
              />
            )}
            {tab === "table" && <ListBilateralPrices />}
          </div>
        )}
        {side === "fixed" && <ManageFixedBilateralPrices />}
      </SideTabs>
    </div>
  );
}

export default ManageBilateralPrices;
